/**
 * Gypi i rekrutimit te ndërfaqja — pasqyrë e rregullave të serverit.
 *
 * Serveri vendos; kjo faqe vetëm nuk ofron butona që do të refuzoheshin. Nëse
 * të dy dalin jashtë sinkronit, fiton serveri — dhe kjo është me qëllim:
 * një buton i dukshëm që kthen gabim është më i mirë se një rregull i fshehur
 * që zbatohet vetëm te klienti.
 *
 * Burimi: `backend/services/recruitmentPipeline.js`.
 */
export const TRANSITIONS = {
  applied:     ['shortlisted', 'interview', 'rejected'],
  shortlisted: ['interview', 'offer', 'rejected'],
  interview:   ['shortlisted', 'interview', 'offer', 'rejected'],
  offer:       ['hired', 'interview', 'rejected'],
  hired:       [],
  rejected:    ['applied', 'shortlisted', 'interview'],
};

export const nextStages = (from) => TRANSITIONS[String(from || 'applied')] || [];

/**
 * Hapat që janë të ligjshëm për TË GJITHA fazat e dhëna — prerja, jo bashkimi.
 *
 * Një veprim masiv mbi kandidatë në faza të ndryshme duhet të ofrojë vetëm atë
 * që kalon për secilin. Bashkimi do të dukej bujar dhe do të prodhonte gjysmën
 * e rreshtave të dështuar; prerja e bën të pamundur zgjedhjen e gabuar.
 *
 * «rejected» lihet jashtë me qëllim: refuzimi ka rrugën e vet, sepse kërkon
 * arsye të strukturuar — serveri e refuzon një kalim të thjeshtë drejt tij.
 */
export function commonNextStages(statuses = []) {
  const list = statuses.filter(Boolean);
  if (!list.length) return [];
  return list
    .map((st) => nextStages(st).filter((x) => x !== 'rejected'))
    .reduce((acc, one) => acc.filter((x) => one.includes(x)));
}

/** Arsyet e refuzimit — të njëjtat kode që pranon serveri (migrimi 087). */
export const REJECTION_REASONS = [
  { code: 'requirements_not_met', label: 'Nuk i plotëson kërkesat' },
  { code: 'experience',           label: 'Përvoja' },
  { code: 'language',             label: 'Gjuha' },
  { code: 'availability',         label: 'Disponueshmëria / orari' },
  { code: 'salary_expectation',   label: 'Pritshmëria për pagën' },
  { code: 'candidate_withdrew',   label: 'Kandidati u tërhoq' },
  { code: 'position_filled',      label: 'Pozita u plotësua' },
  { code: 'no_show',              label: 'Nuk u paraqit' },
  { code: 'other',                label: 'Tjetër' },
];

export const rejectionLabel = (code) =>
  (REJECTION_REASONS.find((r) => r.code === code) || {}).label || code || '—';

/* ── Intervistat ─────────────────────────────────────────────────────────── */
export const INTERVIEW_KINDS = [
  { code: 'onsite', label: 'Në zyrë' },
  { code: 'online', label: 'Online' },
  { code: 'phone',  label: 'Me telefon' },
];

export const INTERVIEW_STATUSES = [
  { code: 'scheduled',   label: 'E planifikuar', color: 'info' },
  { code: 'completed',   label: 'E përfunduar',  color: 'success' },
  { code: 'rescheduled', label: 'E rikaktuar',   color: 'warning' },
  { code: 'cancelled',   label: 'E anuluar',     color: 'default' },
  { code: 'no_show',     label: 'Nuk u paraqit', color: 'error' },
];

export const INTERVIEW_RECOMMENDATIONS = [
  { code: 'proceed',           label: 'Vazhdo' },
  { code: 'another_interview', label: 'Edhe një intervistë' },
  { code: 'offer',             label: 'Bëj ofertë' },
  { code: 'reject',            label: 'Refuzo' },
];

/* ── Ofertat ─────────────────────────────────────────────────────────────── */
export const OFFER_STATUSES = [
  { code: 'draft',            label: 'Draft',          color: 'default' },
  { code: 'pending_approval', label: 'Për miratim',    color: 'warning' },
  { code: 'approved',         label: 'E miratuar',     color: 'info' },
  { code: 'sent',             label: 'E dërguar',      color: 'info' },
  { code: 'accepted',         label: 'E pranuar',      color: 'success' },
  { code: 'declined',         label: 'E refuzuar',     color: 'error' },
  { code: 'expired',          label: 'E skaduar',      color: 'default' },
  { code: 'cancelled',        label: 'E anuluar',      color: 'default' },
];

export const EMPLOYMENT_TYPES = [
  { code: 'full_time',  label: 'Me orar të plotë' },
  { code: 'part_time',  label: 'Me orar të pjesshëm' },
  { code: 'contract',   label: 'Me kontratë' },
  { code: 'seasonal',   label: 'Sezonal' },
  { code: 'internship', label: 'Praktikë' },
];

const pick = (list, code) => list.find((x) => x.code === code) || null;
export const interviewKindLabel   = (c) => pick(INTERVIEW_KINDS, c)?.label || c || '—';
export const interviewStatusMeta  = (c) => pick(INTERVIEW_STATUSES, c) || { code: c, label: c || '—', color: 'default' };
export const recommendationLabel  = (c) => pick(INTERVIEW_RECOMMENDATIONS, c)?.label || c || '—';
export const offerStatusMeta      = (c) => pick(OFFER_STATUSES, c) || { code: c, label: c || '—', color: 'default' };
export const employmentTypeLabel  = (c) => pick(EMPLOYMENT_TYPES, c)?.label || c || '—';
